<template>
  <BaseOutline
    class="max-w-lg w-full flex"
    :class="{ 'relative !bg-primary/25': isPopular }"
  >
    <!-- Popular Badge -->
    <div
      v-if="false && isPopular"
      class="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10"
    >
      <div
        class="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium"
      >
        {{ $t('landing.pricing.popularBadge') }}
      </div>
    </div>

    <div class="flex flex-col w-full">
      <div class="p-8 flex flex-col gap-8 bg-white">
        <div class="text-center">
          <h3 class="text-3xl font-bold mb-2">{{ title }}</h3>
          <p
            class="text-gray-500 mb-8"
            :class="{ 'opacity-0 invisible': !subtitle }"
          >
            {{ subtitle }}
          </p>

          <div class="mb-8">
            <div class="mb-6 flex justify-center">
              <div class="bg-red-50 text-red-600 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 border border-red-100 shadow-sm">
                <BaseIcon icon="time-fast" />
                <span>{{ $t('landing.pricing.timerLabel') }} {{ formattedTimeLeft }}</span>
              </div>
            </div>

            <div class="flex items-baseline justify-center">
              <div v-if="originalPrice" class="relative mr-4">
                <span class="text-2xl text-gray-400 font-medium">{{
                  originalPrice
                }}</span>
                <span
                  class="absolute bottom-[0.8rem] left-0 bg-red-500 h-[2px] w-full -rotate-6"
                />
              </div>

              <div class="flex flex-col items-center">
                <div class="flex items-baseline">
                  <span
                    class="text-7xl font-extrabold text-gray-900 tracking-tight"
                    >{{ price }}</span
                  >
                  <span
                    v-if="currencyLabel"
                    class="text-gray-500 ml-2 text-xl font-medium"
                    >{{ currencyLabel }}</span
                  >
                </div>
                <span
                  v-if="priceApprox"
                  class="text-sm text-gray-500 mt-1"
                >{{ priceApprox }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-5">
          <div
            v-for="(feature, index) in features"
            :key="'feature-' + index"
            class="flex items-start gap-4"
          >
            <div class="mt-1 bg-success/10 rounded-full p-1">
              <BaseIcon icon="check" class="text-success text-sm" />
            </div>

            <span class="text-gray-700 text-lg" v-html="feature.content" />
          </div>
        </div>

        <div class="text-center mt-4">
          <BaseButton
            class="w-full mb-4"
            color="primary-gradient"
            size="big"
            :is-loading="isLoading"
            :is-disabled="isDisabled"
            @click="() => $emit('purchase')"
          >
            {{ buttonText }}
          </BaseButton>

          <div class="flex flex-col items-center justify-center gap-4 text-sm">
            <div
              class="flex items-center gap-2 bg-success/10 text-success font-semibold rounded-full px-4 py-2"
            >
              <BaseIcon icon="refresh" />
              <span>{{ $t('landing.pricing.guarantee') }}</span>
            </div>

            <div class="flex items-center gap-4 text-gray-400">
              <div class="flex items-center gap-1">
                <BaseIcon icon="shield-check" class="text-xs" />
                <span class="text-xs uppercase tracking-wider font-bold">{{
                  $t('landing.pricing.securePayment')
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseOutline>
</template>

<script>
import BaseOutline from '@/components/Base/BaseOutline.vue'
import BaseIcon from '@/components/Base/BaseIcon.vue'
import BaseButton from '@/components/Base/BaseButton.vue'

export default {
  name: 'LandingPricingCard',

  components: { BaseOutline, BaseIcon, BaseButton },

  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    originalPrice: { type: String, default: null },
    price: { type: String, required: true },
    currencyLabel: { type: String, default: '' },
    priceApprox: { type: String, default: '' },
    features: { type: Array, required: true },
    buttonText: { type: String, required: true },
    isPopular: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false }
  },

  data() {
    return {
      timeLeft: 0,
      timerInterval: null
    }
  },

  computed: {
    formattedTimeLeft() {
      const h = Math.floor(this.timeLeft / (1000 * 60 * 60))
      const m = Math.floor((this.timeLeft % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor((this.timeLeft % (1000 * 60)) / 1000)
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    }
  },

  mounted() {
    this.startTimer()
  },

  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  },

  methods: {
    startTimer() {
      const CYCLE_MS = 72 * 60 * 60 * 1000
      let startTime = localStorage.getItem('pricing_timer_start')
      
      if (!startTime) {
        startTime = Date.now()
        localStorage.setItem('pricing_timer_start', startTime)
      }
      
      const updateTimer = () => {
        const now = Date.now()
        let elapsed = now - parseInt(startTime, 10)
        
        if (elapsed >= CYCLE_MS) {
          startTime = now
          localStorage.setItem('pricing_timer_start', startTime)
          elapsed = 0
        }
        
        this.timeLeft = CYCLE_MS - elapsed
      }
      
      updateTimer()
      this.timerInterval = setInterval(updateTimer, 1000)
    }
  }
}
</script>
